import React, { useState, useEffect, useMemo } from "react";
import {
  Menu,
  X,
  Building2,
  Briefcase,
  HeartHandshake,
  LineChart,
  Landmark,
  Network,
  Layers,
  Sprout,
  Cpu,
  Languages,
  Users,
  Brain,
  Check,
  Mail,
  Facebook,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";
import { translations } from "@/pages/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_IDS = ["apropos", "parcours", "expertise", "atouts", "contact"];

const LanguageToggle = ({ lang, setLang, variant = "desktop" }) => (
  <div
    data-testid={`lang-toggle-${variant}`}
    className="inline-flex items-center gap-2 text-[11px] font-sans font-medium tracking-[0.2em] select-none"
  >
    <button
      data-testid="lang-fr-button"
      onClick={() => setLang("fr")}
      className={`uppercase transition-colors ${
        lang === "fr"
          ? "text-[#0F172A]"
          : "text-[#64748B] hover:text-[#0F172A]"
      }`}
      aria-pressed={lang === "fr"}
    >
      FR
    </button>
    <span className="text-[#CBD5E1]">|</span>
    <button
      data-testid="lang-en-button"
      onClick={() => setLang("en")}
      className={`uppercase transition-colors ${
        lang === "en"
          ? "text-[#0F172A]"
          : "text-[#64748B] hover:text-[#0F172A]"
      }`}
      aria-pressed={lang === "en"}
    >
      EN
    </button>
  </div>
);

const Header = ({ t, lang, setLang }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-[#E2E8F0]"
          : "bg-white/85 backdrop-blur-sm border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <button
            data-testid="brand-home-link"
            onClick={() => handleNav("apropos")}
            className="text-left leading-tight shrink-0 mr-4"
          >
            <span className="block font-serif font-bold text-lg md:text-xl lg:text-[22px] tracking-tight text-[#0F172A] whitespace-nowrap">
              {t.brand}
            </span>
            <span className="block font-sans font-light text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-[#94A3B8] mt-1 whitespace-nowrap">
              {t.role}
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-5 xl:gap-10">
            <nav className="flex items-center gap-5 xl:gap-10">
              {NAV_IDS.map((id) => (
                <button
                  key={id}
                  data-testid={`nav-${id}-link`}
                  onClick={() => handleNav(id)}
                  className="text-[11px] font-sans font-medium uppercase tracking-[0.2em] text-[#64748B] hover:text-[#0F172A] transition-colors duration-200 whitespace-nowrap"
                >
                  {t.nav[id]}
                </button>
              ))}
            </nav>
            <span className="block w-px h-5 bg-[#E2E8F0]" />
            <LanguageToggle lang={lang} setLang={setLang} variant="desktop" />
          </div>

          <div className="flex items-center gap-4 lg:hidden">
            <LanguageToggle lang={lang} setLang={setLang} variant="mobile" />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="text-[#0F172A] p-2"
              aria-label="Menu"
            >
              {open ? (
                <X size={22} strokeWidth={1.5} />
              ) : (
                <Menu size={22} strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden border-t border-[#E2E8F0] py-4"
          >
            <nav className="flex flex-col">
              {NAV_IDS.map((id) => (
                <button
                  key={id}
                  data-testid={`mobile-nav-${id}-link`}
                  onClick={() => handleNav(id)}
                  className="text-left py-3 text-xs font-sans font-medium uppercase tracking-[0.2em] text-[#64748B] hover:text-[#0F172A] border-b border-[#E2E8F0] last:border-b-0"
                >
                  {t.nav[id]}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

const Monogram = ({ t }) => (
  <div className="w-full max-w-[360px] mx-auto md:mx-0">
    <div
      data-testid="profile-photo"
      className="relative aspect-[4/5] w-full bg-[#F8FAFC] border border-[#E2E8F0] overflow-hidden"
    >
      <img
        src="https://customer-assets.emergentagent.com/job_boongo-portfolio/artifacts/xio2ef06_11111.jpg"
        alt="Pancrace Boongo Jr."
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: "50% 22%" }}
      />
    </div>
    <p
      data-testid="monogram-signature"
      className="mt-5 text-center font-serif font-medium text-sm md:text-base tracking-[0.3em] text-[#0F172A]"
    >
      PB JR
    </p>
  </div>
);

const AboutSection = ({ t }) => (
  <section
    id="apropos"
    data-testid="section-apropos"
    className="pt-28 md:pt-32 pb-16 md:pb-20"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="md:col-span-5 lg:col-span-4">
          <Monogram t={t} />
        </div>
        <div className="md:col-span-7 lg:col-span-8">
          <span className="block text-xs md:text-sm uppercase tracking-[0.3em] font-sans font-semibold text-[#0F172A] mb-8">
            {t.about.overline}
          </span>
          <div className="space-y-5 text-base md:text-[17px] font-sans text-[#475569] leading-relaxed max-w-3xl text-justify hyphens-auto">
            {t.about.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="mt-10 max-w-3xl flex justify-end">
            <a
              href="https://web.facebook.com/pancrace.boongo.Jr/?locale=fr_FR"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="about-facebook-link"
              className="inline-flex items-center justify-center w-10 h-10 border border-[#E2E8F0] text-[#64748B] hover:text-white hover:bg-[#1877F2] hover:border-[#1877F2] transition-colors duration-200"
            >
              <Facebook size={18} strokeWidth={1.6} />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ParcoursCard = ({ icon: Icon, overline, title, items, description, testId }) => (
  <div
    data-testid={testId}
    className="bg-white border border-[#E2E8F0] p-7 lg:p-9 flex flex-col h-full transition-colors duration-300 hover:bg-[#F8FAFC]"
  >
    <Icon size={26} strokeWidth={1.4} className="text-[#0F172A] mb-6" />
    <span className="text-[10px] font-sans uppercase tracking-[0.3em] text-[#64748B] mb-3">
      {overline}
    </span>
    <h3 className="font-serif text-xl md:text-2xl text-[#0F172A] mb-4 leading-tight">
      {title}
    </h3>
    {description && (
      <p className="font-sans text-[#64748B] leading-relaxed text-sm md:text-[15px] mb-5 text-left">
        {description}
      </p>
    )}
    {items && items.length > 0 && (
      <ul className="space-y-2.5 mt-2">
        {items.map((item) => (
          <li
            key={item}
            className="text-sm md:text-[15px] font-sans text-[#0F172A] border-l-2 border-[#0F172A]/15 pl-4 leading-snug"
          >
            {item}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const ParcoursSection = ({ t }) => (
  <section
    id="parcours"
    data-testid="section-parcours"
    className="py-16 md:py-20 bg-[#F8FAFC] border-y border-[#E2E8F0]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-12">
        <div className="lg:col-span-5">
          <span className="block text-xs md:text-sm uppercase tracking-[0.3em] font-sans font-semibold text-[#0F172A] mb-5">
            {t.parcours.overline}
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-[40px] tracking-tight text-[#0F172A] leading-[1.15]">
            {t.parcours.title}
          </h2>
        </div>
        <div className="lg:col-span-7 lg:pt-2">
          <p className="text-base md:text-[17px] font-sans text-[#475569] leading-relaxed text-justify hyphens-auto">
            {t.parcours.intro}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
        <ParcoursCard
          testId="card-institutions"
          icon={Landmark}
          overline={t.parcours.cards.institutions.overline}
          title={t.parcours.cards.institutions.title}
          items={t.parcours.cards.institutions.items}
        />
        <ParcoursCard
          testId="card-entrepreneuriat"
          icon={Briefcase}
          overline={t.parcours.cards.entrepreneuriat.overline}
          title={t.parcours.cards.entrepreneuriat.title}
          description={t.parcours.cards.entrepreneuriat.description}
          items={t.parcours.cards.entrepreneuriat.items}
        />
        <ParcoursCard
          testId="card-engagement"
          icon={HeartHandshake}
          overline={t.parcours.cards.engagement.overline}
          title={t.parcours.cards.engagement.title}
          description={t.parcours.cards.engagement.description}
        />
      </div>
    </div>
  </section>
);

const EXPERTISE_ICONS = [LineChart, Building2, Network, Layers, Sprout, Cpu];

const ExpertiseSection = ({ t }) => (
  <section
    id="expertise"
    data-testid="section-expertise"
    className="py-16 md:py-20"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
      <div className="max-w-3xl mb-12">
        <span className="block text-xs md:text-sm uppercase tracking-[0.3em] font-sans font-semibold text-[#0F172A] mb-5">
          {t.expertise.overline}
        </span>
        <h2 className="font-serif text-3xl md:text-4xl lg:text-[40px] tracking-tight text-[#0F172A] leading-[1.15] mb-6">
          {t.expertise.title}
        </h2>
        <p className="text-base md:text-[17px] font-sans text-[#475569] leading-relaxed text-justify hyphens-auto">
          {t.expertise.intro}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border border-[#E2E8F0]">
        {t.expertise.items.map((item, idx) => {
          const Icon = EXPERTISE_ICONS[idx] || LineChart;
          return (
            <div
              key={idx}
              data-testid={`expertise-card-${idx}`}
              className="p-7 lg:p-9 bg-white border-[#E2E8F0] border-b md:border-b lg:border-b border-r-0 md:border-r last:border-b-0 md:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r lg:[&:nth-child(3n)]:border-r-0 md:[&:nth-last-child(-n+2)]:border-b-0 lg:[&:nth-last-child(-n+3)]:border-b-0 transition-colors duration-300 hover:bg-[#F8FAFC]"
            >
              <div className="flex items-center justify-between mb-6">
                <Icon size={26} strokeWidth={1.4} className="text-[#0F172A]" />
                <span className="text-[10px] font-sans uppercase tracking-[0.3em] text-[#64748B]">
                  {String(idx + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="font-serif text-xl md:text-2xl text-[#0F172A] mb-3 leading-tight">
                {item.title}
              </h3>
              <p className="font-sans text-[#64748B] leading-relaxed text-sm md:text-[15px] text-left">
                {item.text}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const AtoutCard = ({ icon: Icon, title, description, items, testId }) => (
  <div
    data-testid={testId}
    className="border border-white/10 bg-white/[0.03] p-7 lg:p-9 flex flex-col h-full transition-colors duration-300 hover:bg-white/[0.06]"
  >
    <Icon size={26} strokeWidth={1.4} className="text-white mb-6" />
    <h3 className="font-serif text-xl md:text-2xl text-white mb-3 leading-tight">
      {title}
    </h3>
    <p className="font-sans text-slate-300 leading-relaxed text-sm md:text-[15px] mb-5 text-left">
      {description}
    </p>
    <ul className="space-y-2.5 mt-1">
      {items.map((item) => (
        <li
          key={item}
          className="flex items-start gap-3 text-sm md:text-[15px] font-sans text-slate-200"
        >
          <Check size={15} strokeWidth={1.8} className="text-white/70 mt-1 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const AtoutsSection = ({ t }) => (
  <section
    id="atouts"
    data-testid="section-atouts"
    className="py-16 md:py-20 bg-[#0F172A] text-white"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-12">
        <div className="lg:col-span-5">
          <span className="block text-xs md:text-sm uppercase tracking-[0.3em] font-sans font-semibold text-white/80 mb-5">
            {t.atouts.overline}
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-[40px] tracking-tight text-white leading-[1.15]">
            {t.atouts.title}
          </h2>
        </div>
        <div className="lg:col-span-7 lg:pt-2">
          <p className="text-base md:text-[17px] font-sans text-slate-300 leading-relaxed text-justify hyphens-auto">
            {t.atouts.intro}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
        <AtoutCard
          testId="card-communication"
          icon={Languages}
          title={t.atouts.cards.communication.title}
          description={t.atouts.cards.communication.description}
          items={t.atouts.cards.communication.items}
        />
        <AtoutCard
          testId="card-leadership"
          icon={Users}
          title={t.atouts.cards.leadership.title}
          description={t.atouts.cards.leadership.description}
          items={t.atouts.cards.leadership.items}
        />
        <AtoutCard
          testId="card-analyse"
          icon={Brain}
          title={t.atouts.cards.analyse.title}
          description={t.atouts.cards.analyse.description}
          items={t.atouts.cards.analyse.items}
        />
      </div>
    </div>
  </section>
);

const ContactSection = ({ t }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t.contact.errors.empty);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success(t.contact.toastSuccess);
    } catch (err) {
      const detail = err?.response?.data?.detail || t.contact.errors.generic;
      toast.error(typeof detail === "string" ? detail : t.contact.errors.generic);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="py-16 md:py-20"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-10">
          <span className="block text-xs md:text-sm uppercase tracking-[0.3em] font-sans font-semibold text-[#0F172A] mb-5">
            {t.contact.overline}
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-[40px] tracking-tight text-[#0F172A] leading-[1.15] mb-5">
            {t.contact.title}
          </h2>
          <p className="text-base md:text-[17px] font-sans text-[#475569] leading-relaxed max-w-xl mx-auto">
            {t.contact.intro}
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-sm font-sans text-[#0F172A]">
            <Mail size={16} strokeWidth={1.5} />
            <a
              href="mailto:contact@pancraceboongojr.com"
              className="hover:underline underline-offset-4"
              data-testid="contact-email-link"
            >
              contact@pancraceboongojr.com
            </a>
          </div>
        </div>

        {sent ? (
          <div
            data-testid="contact-success-message"
            className="border border-[#E2E8F0] bg-[#F8FAFC] p-10 text-center"
          >
            <Check size={28} strokeWidth={1.5} className="text-[#0F172A] mx-auto mb-4" />
            <p className="font-serif text-xl md:text-2xl text-[#0F172A] leading-snug mb-2">
              {t.contact.success.title}
            </p>
            <p className="font-sans text-[#64748B]">{t.contact.success.subtitle}</p>
            <button
              data-testid="contact-send-another"
              onClick={() => setSent(false)}
              className="mt-8 text-[11px] font-sans font-medium uppercase tracking-[0.25em] text-[#0F172A] hover:opacity-70 transition-opacity"
            >
              {t.contact.success.another}
            </button>
          </div>
        ) : (
          <form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            className="space-y-5"
            noValidate
          >
            <div>
              <Label
                htmlFor="contact-name"
                className="text-[11px] font-sans font-medium uppercase tracking-[0.25em] text-[#0F172A] mb-2 block"
              >
                {t.contact.labels.name}
              </Label>
              <Input
                id="contact-name"
                name="name"
                value={form.name}
                onChange={handleChange}
                data-testid="contact-input-name"
                placeholder={t.contact.placeholders.name}
                required
                className="rounded-none border-[#E2E8F0] bg-[#F8FAFC] py-5 px-4 text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:ring-1 focus-visible:ring-[#0F172A] focus-visible:border-[#0F172A]"
              />
            </div>
            <div>
              <Label
                htmlFor="contact-email"
                className="text-[11px] font-sans font-medium uppercase tracking-[0.25em] text-[#0F172A] mb-2 block"
              >
                {t.contact.labels.email}
              </Label>
              <Input
                id="contact-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                data-testid="contact-input-email"
                placeholder={t.contact.placeholders.email}
                required
                className="rounded-none border-[#E2E8F0] bg-[#F8FAFC] py-5 px-4 text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:ring-1 focus-visible:ring-[#0F172A] focus-visible:border-[#0F172A]"
              />
            </div>
            <div>
              <Label
                htmlFor="contact-message"
                className="text-[11px] font-sans font-medium uppercase tracking-[0.25em] text-[#0F172A] mb-2 block"
              >
                {t.contact.labels.message}
              </Label>
              <Textarea
                id="contact-message"
                name="message"
                value={form.message}
                onChange={handleChange}
                data-testid="contact-input-message"
                placeholder={t.contact.placeholders.message}
                required
                rows={5}
                className="rounded-none border-[#E2E8F0] bg-[#F8FAFC] py-3 px-4 text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:ring-1 focus-visible:ring-[#0F172A] focus-visible:border-[#0F172A] resize-none"
              />
            </div>
            <Button
              type="submit"
              data-testid="contact-submit-button"
              disabled={submitting}
              className="rounded-none bg-[#0F172A] text-white hover:bg-[#1e293b] px-10 py-5 text-[11px] uppercase tracking-[0.25em] font-semibold transition-colors w-full md:w-auto disabled:opacity-60"
            >
              {submitting ? t.contact.submitting : t.contact.submit}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
};

const QuoteSection = ({ t }) => (
  <section
    data-testid="section-quote"
    className="py-16 md:py-20 bg-[#F8FAFC] border-y border-[#E2E8F0]"
  >
    <div className="max-w-2xl mx-auto px-6 md:px-8 text-center">
      <blockquote className="font-serif italic text-2xl md:text-3xl text-[#0F172A] leading-[1.35]">
        « {t.quote.text} »
      </blockquote>
      <p
        data-testid="quote-author"
        translate="no"
        className="notranslate mt-6 font-serif text-base md:text-lg text-[#0F172A]"
      >
        — {t.quote.author}
      </p>
    </div>
  </section>
);

const Footer = ({ t }) => (
  <footer data-testid="site-footer" className="bg-white border-t border-[#E2E8F0]">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-8">
      <p className="font-sans text-xs md:text-sm text-[#64748B] text-center tracking-wide">
        {t.footer}
      </p>
    </div>
  </footer>
);

export default function Portfolio() {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "fr";
    try {
      const saved = window.localStorage.getItem("pbjr-lang");
      if (saved === "fr" || saved === "en") return saved;
    } catch (_e) {
      /* ignore */
    }
    return "fr";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem("pbjr-lang", lang);
    } catch (_e) {
      /* ignore */
    }
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useMemo(() => translations[lang], [lang]);

  return (
    <div
      data-testid="portfolio-root"
      data-lang={lang}
      translate="no"
      className="notranslate min-h-screen bg-white text-[#0F172A]"
    >
      <Header t={t} lang={lang} setLang={setLang} />
      <main>
        <AboutSection t={t} />
        <ParcoursSection t={t} />
        <ExpertiseSection t={t} />
        <AtoutsSection t={t} />
        <ContactSection t={t} />
        <QuoteSection t={t} />
      </main>
      <Footer t={t} />
      <Toaster position="top-center" />
    </div>
  );
}
